import type { TemplateExerciseOutput } from '../validation/template.schemas';

export interface ExerciseGroup {
  leaderId: string;
  leaderName: string;
  type: 'superset' | 'triset' | 'giant_set';
  exerciseUuids: string[];
}

export type GroupType = 'superset' | 'triset' | 'giant_set';

/** What a group card is headed with. */
export const GROUP_TYPE_LABELS: Record<GroupType, string> = {
  superset: 'SUPERSET',
  triset: 'TRI-SET',
  giant_set: 'GIANT SET',
};

export function deriveGroupType(count: number): GroupType | null {
  if (count < 2) return null;
  if (count === 2) return 'superset';
  if (count === 3) return 'triset';
  return 'giant_set';
}

/**
 * Groups exercises for display.
 *
 * Data model (matches backend validation):
 * - Leader: group_leader = null, group_type = 'superset'|'triset'|'giant_set'
 * - Member: group_leader = leaderUuid, group_type = null
 * - Standalone: group_leader = null, group_type = null
 */
export function groupExercises(exercises: TemplateExerciseOutput[]): {
  groups: ExerciseGroup[];
  standalone: TemplateExerciseOutput[];
} {
  // Collect members by their group_leader
  const membersByLeader = new Map<string, TemplateExerciseOutput[]>();

  for (const exercise of exercises) {
    if (exercise.group_leader) {
      const existing = membersByLeader.get(exercise.group_leader);
      if (existing) {
        existing.push(exercise);
      } else {
        membersByLeader.set(exercise.group_leader, [exercise]);
      }
    }
  }

  const groups: ExerciseGroup[] = [];
  const groupedUuids = new Set<string>();

  for (const [leaderId, members] of membersByLeader) {
    // Find the leader exercise (has group_type, no group_leader)
    const leader = exercises.find((e) => e.uuid === leaderId);

    if (!leader) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[exercise-groups] Orphaned group members: leader "${leaderId}" not found. ` +
            `${members.length} member(s) will be treated as standalone.`
        );
      }
      continue;
    }

    // A leader that is itself a member — a self-reference, a circular pair, or
    // a leader chained under another group — cannot head a group: rendering it
    // would put the same exercise in two places. Its members fall back to
    // standalone, and the exercise renders once, wherever it validly belongs.
    if (leader.group_leader) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[exercise-groups] Invalid group leader "${leaderId}": it points at ` +
            `"${leader.group_leader}" itself. ${members.length} member(s) will be ` +
            `treated as standalone.`
        );
      }
      continue;
    }

    // Total group size = leader + members
    const totalCount = 1 + members.length;
    const rawGroupType = leader.group_type ?? deriveGroupType(totalCount);

    // Only create ExerciseGroup for superset-related types
    const groupType =
      rawGroupType === 'superset' ||
      rawGroupType === 'triset' ||
      rawGroupType === 'giant_set'
        ? rawGroupType
        : deriveGroupType(totalCount);

    if (totalCount >= 2 && groupType) {
      // Preserve original order from exercises array
      const orderedUuids = exercises
        .filter((e) => e.uuid === leaderId || e.group_leader === leaderId)
        .map((e) => e.uuid);

      groups.push({
        leaderId,
        leaderName: leader.name,
        type: groupType,
        exerciseUuids: orderedUuids,
      });

      // Track all grouped exercises (leader + members)
      groupedUuids.add(leaderId);
      for (const member of members) {
        groupedUuids.add(member.uuid);
      }
    }
  }

  // Standalone = exercises not part of any valid group
  const standalone = exercises.filter((e) => !groupedUuids.has(e.uuid));

  return { groups, standalone };
}

/**
 * Whether an exercise currently belongs to a group, as a member or as a leader.
 */
function isGrouped(exercises: TemplateExerciseOutput[], uuid: string): boolean {
  const exercise = exercises.find((e) => e.uuid === uuid);
  if (!exercise) return false;
  return Boolean(exercise.group_leader || exercise.group_type);
}

/**
 * Detaches each exercise from whatever group it is in, so it can join another.
 *
 * An exercise belongs to at most one group. Without this, grouping a member
 * left its old leader carrying a `group_type` with no members, and grouping a
 * leader left its old members pointing at an exercise that is now a member
 * itself — which rendered that exercise twice.
 */
function detachFromGroups(
  exercises: TemplateExerciseOutput[],
  uuids: string[]
): TemplateExerciseOutput[] {
  return uuids.reduce(
    (list, uuid) =>
      isGrouped(list, uuid) ? removeFromGroup(list, uuid) : list,
    exercises
  );
}

/**
 * Creates a new exercise group (superset/triset/giant_set).
 *
 * Selected exercises that already belong to a group leave it first — the old
 * group shrinks, re-leads or dissolves as `removeFromGroup` decides.
 *
 * Data model (matches backend validation):
 * - Leader: group_leader = null, group_type = derived from count
 * - Members: group_leader = leaderUuid, group_type = null
 */
export function createExerciseGroup(
  exercises: TemplateExerciseOutput[],
  selectedUuids: string[],
  leaderUuid: string
): TemplateExerciseOutput[] {
  if (selectedUuids.length < 2) return exercises;
  if (!selectedUuids.includes(leaderUuid)) return exercises;

  const groupType = deriveGroupType(selectedUuids.length);

  return detachFromGroups(exercises, selectedUuids).map((exercise) => {
    if (!selectedUuids.includes(exercise.uuid)) {
      return exercise;
    }

    if (exercise.uuid === leaderUuid) {
      // Leader: NO group_leader, HAS group_type
      return {
        ...exercise,
        group_leader: null,
        group_type: groupType ?? undefined,
      };
    }

    // Member: HAS group_leader pointing to leader, NO group_type
    return {
      ...exercise,
      group_leader: leaderUuid,
      group_type: undefined,
    };
  });
}

/**
 * Dissolves an exercise group, making all exercises standalone.
 */
export function dissolveGroup(
  exercises: TemplateExerciseOutput[],
  groupLeaderUuid: string
): TemplateExerciseOutput[] {
  return exercises.map((exercise) => {
    // Clear leader's group_type
    if (exercise.uuid === groupLeaderUuid) {
      return {
        ...exercise,
        group_leader: null,
        group_type: undefined,
      };
    }

    // Clear members' group_leader
    if (exercise.group_leader === groupLeaderUuid) {
      return {
        ...exercise,
        group_leader: null,
        group_type: undefined,
      };
    }

    return exercise;
  });
}

/**
 * Adds an exercise to an existing group.
 *
 * If the exercise is already in a different group it leaves that one first.
 * Adding the leader, or an existing member, to its own group changes nothing.
 */
export function addToGroup(
  exercises: TemplateExerciseOutput[],
  exerciseUuid: string,
  groupLeaderUuid: string
): TemplateExerciseOutput[] {
  if (exerciseUuid === groupLeaderUuid) return exercises;
  const existing = exercises.find((e) => e.uuid === exerciseUuid);
  if (existing?.group_leader === groupLeaderUuid) return exercises;

  exercises = detachFromGroups(exercises, [exerciseUuid]);

  // Count current members (exercises pointing to this leader)
  const currentMembers = exercises.filter(
    (e) => e.group_leader === groupLeaderUuid
  );
  // Total = 1 (leader) + current members + 1 (new member)
  const newTotalCount = 1 + currentMembers.length + 1;
  const newGroupType = deriveGroupType(newTotalCount);

  return exercises.map((exercise) => {
    // Add new member
    if (exercise.uuid === exerciseUuid) {
      return {
        ...exercise,
        group_leader: groupLeaderUuid,
        group_type: undefined,
      };
    }

    // Update leader's group_type
    if (exercise.uuid === groupLeaderUuid) {
      return {
        ...exercise,
        group_type: newGroupType ?? undefined,
      };
    }

    return exercise;
  });
}

/**
 * Removes an exercise from its group.
 * If removing a leader, promotes another member to leader.
 * If group would have < 2 exercises, dissolves the group.
 */
export function removeFromGroup(
  exercises: TemplateExerciseOutput[],
  exerciseUuid: string
): TemplateExerciseOutput[] {
  const exerciseToRemove = exercises.find((e) => e.uuid === exerciseUuid);
  if (!exerciseToRemove) return exercises;

  // Determine if this is a leader or a member
  // Leader: has group_type set, no group_leader
  // Member: has group_leader set
  const isLeader =
    Boolean(exerciseToRemove.group_type) && !exerciseToRemove.group_leader;

  let groupLeaderUuid: string;

  if (isLeader) {
    groupLeaderUuid = exerciseUuid;
  } else if (exerciseToRemove.group_leader) {
    groupLeaderUuid = exerciseToRemove.group_leader;
  } else {
    // Not part of any group
    return exercises;
  }

  // Get current members (excluding the exercise being removed if it's a member)
  const currentMembers = exercises.filter(
    (e) => e.group_leader === groupLeaderUuid && e.uuid !== exerciseUuid
  );

  // Calculate remaining count after removal
  // If removing leader: only members remain (need at least 2 to form new group)
  // If removing member: 1 (leader) + remaining members
  const remainingCount = isLeader
    ? currentMembers.length
    : 1 + currentMembers.length;

  // If less than 2 remain, dissolve the group
  if (remainingCount < 2) {
    return dissolveGroup(exercises, groupLeaderUuid);
  }

  // If removing the leader, promote a new leader
  if (isLeader) {
    const newLeader = currentMembers[0];
    if (!newLeader) return dissolveGroup(exercises, groupLeaderUuid);

    const newGroupType = deriveGroupType(currentMembers.length);

    return exercises.map((exercise) => {
      // Old leader becomes standalone
      if (exercise.uuid === exerciseUuid) {
        return {
          ...exercise,
          group_leader: null,
          group_type: undefined,
        };
      }

      // Update members to point to new leader
      if (exercise.group_leader === groupLeaderUuid) {
        const isNewLeader = exercise.uuid === newLeader.uuid;
        return {
          ...exercise,
          // New leader: no group_leader, has group_type
          // Other members: point to new leader
          group_leader: isNewLeader ? null : newLeader.uuid,
          group_type: isNewLeader ? (newGroupType ?? undefined) : undefined,
        };
      }

      return exercise;
    });
  }

  // Removing a member (not the leader)
  const newGroupType = deriveGroupType(remainingCount);

  return exercises.map((exercise) => {
    // Member being removed becomes standalone
    if (exercise.uuid === exerciseUuid) {
      return {
        ...exercise,
        group_leader: null,
        group_type: undefined,
      };
    }

    // Update leader's group_type
    if (exercise.uuid === groupLeaderUuid) {
      return {
        ...exercise,
        group_type: newGroupType ?? undefined,
      };
    }

    return exercise;
  });
}

/**
 * Removes an exercise from its group and places it at `insertIndex` in the
 * list — one atomic step, for a drag that pulls an exercise out of a group and
 * drops it somewhere else.
 *
 * The group rules are {@link removeFromGroup}'s: removing a member leaves the
 * rest grouped, or dissolves the group when fewer than two would remain;
 * removing the leader promotes the first member, or dissolves the group when
 * only one member is left. An exercise that is not in a group is returned
 * untouched (the same array), so callers can tell nothing changed.
 */
export function removeFromGroupAtPosition(
  exercises: TemplateExerciseOutput[],
  exerciseUuid: string,
  insertIndex: number
): TemplateExerciseOutput[] {
  const exercise = exercises.find((e) => e.uuid === exerciseUuid);
  if (!exercise) return exercises;

  // Determine if this exercise is part of a group
  const isGroupLeader = Boolean(exercise.group_type && !exercise.group_leader);
  const isGroupMember = Boolean(exercise.group_leader);

  // If not in a group, nothing to do
  if (!isGroupLeader && !isGroupMember) return exercises;

  let updatedExercises: TemplateExerciseOutput[];

  if (isGroupMember) {
    // Case 1: Member being removed from group
    const groupLeaderUuid = exercise.group_leader!;

    // Remove exercise from group (clear group_leader)
    updatedExercises = exercises.map((e) =>
      e.uuid === exerciseUuid
        ? { ...e, group_leader: undefined, group_type: undefined }
        : e
    );

    // Check if group needs auto-dissolution (< 2 members remaining)
    const remainingMembers = updatedExercises.filter(
      (e) => e.group_leader === groupLeaderUuid || e.uuid === groupLeaderUuid
    );

    if (remainingMembers.length < 2) {
      // Dissolve: clear group_type from leader, group_leader from any stragglers
      updatedExercises = updatedExercises.map((e) => {
        if (e.uuid === groupLeaderUuid) {
          return { ...e, group_type: undefined };
        }
        if (e.group_leader === groupLeaderUuid) {
          return { ...e, group_leader: undefined };
        }
        return e;
      });
    }
  } else {
    // Case 2: Leader being removed from group
    // Find all members of this group
    const members = exercises.filter((e) => e.group_leader === exerciseUuid);

    if (members.length === 0) {
      // No members, just clear leader's group_type
      updatedExercises = exercises.map((e) =>
        e.uuid === exerciseUuid ? { ...e, group_type: undefined } : e
      );
    } else if (members.length === 1) {
      // Only one member remains - dissolve the group entirely
      updatedExercises = exercises.map((e) => {
        if (e.uuid === exerciseUuid) {
          return { ...e, group_type: undefined };
        }
        if (e.group_leader === exerciseUuid) {
          return { ...e, group_leader: undefined };
        }
        return e;
      });
    } else {
      // Multiple members - promote first member to leader
      const newLeader = members[0]!;
      updatedExercises = exercises.map((e) => {
        if (e.uuid === exerciseUuid) {
          // Remove group_type from old leader
          return { ...e, group_type: undefined };
        }
        if (e.uuid === newLeader.uuid) {
          // Promote to leader: set group_type, clear group_leader
          return {
            ...e,
            group_type: exercise.group_type,
            group_leader: undefined,
          };
        }
        if (e.group_leader === exerciseUuid) {
          // Update other members to point to new leader
          return { ...e, group_leader: newLeader.uuid };
        }
        return e;
      });
    }
  }

  // A group that lost an exercise is a size smaller: a tri-set of two is a
  // superset. Re-derive each remaining leader's type from its member count.
  updatedExercises = updatedExercises.map((e) => {
    if (!e.group_type || e.group_leader) return e;
    // Only the size-named types follow the count; a circuit stays a circuit.
    if (!(e.group_type in GROUP_TYPE_LABELS)) return e;
    const size =
      1 + updatedExercises.filter((m) => m.group_leader === e.uuid).length;
    const groupType = deriveGroupType(size);
    return groupType && groupType !== e.group_type
      ? { ...e, group_type: groupType }
      : e;
  });

  // Reposition exercise at insertIndex
  const exerciseToMove = updatedExercises.find((e) => e.uuid === exerciseUuid)!;
  const withoutExercise = updatedExercises.filter(
    (e) => e.uuid !== exerciseUuid
  );
  const clampedIndex = Math.max(
    0,
    Math.min(insertIndex, withoutExercise.length)
  );
  withoutExercise.splice(clampedIndex, 0, exerciseToMove);

  return withoutExercise;
}
